import { ArrowsClockwiseIcon, BroadcastIcon, CheckIcon, XIcon } from '@phosphor-icons/react'
import { type CSSProperties, useEffect, useState } from 'react'
import Select from 'react-select'

import API from '../../../../api/api'
import Loader from '../../../Loader/Loader'
import ModalContainer from '../../../ModalContainer/ModelContainer'
import Stepper from '../../../Stepper/Stepper'

const STEPS = ['Preparing', 'Select fob/card #', 'Review fob/card', 'Result']

const STEP = {
  PREPARE: 0,
  RESULT: 3,
  REVIEW: 2,
  SELECT_FOB: 1,
} as const

type FobStep = (typeof STEP)[keyof typeof STEP]

interface Fob {
  id: number
  badge: string
  card_format: string
  location_id: number
  created_at: string
  updated_at: string
}

interface FobOption {
  value: number
  label: string
}

const fobSelectStyles = {
  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
}

interface RemoteLockIssueFobModalProps {
  isOpen: boolean
  buildingId: number
  userId: number
  onClose: () => void
  onSuccess: () => void
}

const RemoteLockIssueFobModal = ({
  isOpen,
  buildingId,
  userId,
  onClose,
  onSuccess,
}: RemoteLockIssueFobModalProps) => {
  const [currentStep, setCurrentStep] = useState<FobStep>(STEP.PREPARE)
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [availableFobs, setAvailableFobs] = useState<FobOption[]>([])
  const [selectedFob, setSelectedFob] = useState<FobOption | null>(null)
  const [isFobsLoading, setIsFobsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const fetchAvailableFobs = async (): Promise<void> => {
    setIsFobsLoading(true)
    try {
      const { data } = await API.getRemoteLockBuildingFobs(buildingId)
      setAvailableFobs((data as Fob[]).map((fob) => ({ label: fob.badge, value: fob.id })))
    } catch {
      setAvailableFobs([])
    } finally {
      setIsFobsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEP.PREPARE)
      setIsSuccess(null)
      setIsProcessing(false)
      setSelectedFob(null)
      setErrorMessage(null)
      fetchAvailableFobs()
    }
  }, [isOpen])

  const extractErrorMessage = (error: unknown): string => {
    const axiosError = error as { response?: { data?: { message?: string } } }
    return (
      axiosError?.response?.data?.message ??
      'Temporary technical problem. Please contact IT or try again later.'
    )
  }

  const attemptIssueFob = async (): Promise<boolean> => {
    try {
      await API.issueRemoteLockFob(buildingId, userId, selectedFob?.value)
      setIsSuccess(true)
      onSuccess()
      return true
    } catch (error) {
      setIsSuccess(false)
      setErrorMessage(extractErrorMessage(error))
      return false
    }
  }

  const handleNextFromPrepare = (): void => {
    setCurrentStep(STEP.SELECT_FOB)
  }

  const handleNextFromSelectFob = async (): Promise<void> => {
    setCurrentStep(STEP.REVIEW)
    setIsProcessing(true)
    await attemptIssueFob()
    setIsProcessing(false)
    setCurrentStep(STEP.RESULT)
  }

  const handleTryAgain = (): void => {
    setCurrentStep(STEP.REVIEW)
    setIsSuccess(null)
    setErrorMessage(null)
  }

  const renderContent = () => {
    if (currentStep === STEP.PREPARE) {
      return (
        <>
          <p className="remote-lock-fob-modal__message">
            Please put the fob/card on the encoder.
            <br />
            Wait until it gives 3 beeps, then remove the fob/card from the encoder.
          </p>
          <div className="remote-lock-fob-modal__icon-area remote-lock-fob-modal__icon-area--compact">
            <img
              src="/images/integrations/encoder.png"
              alt="encoder"
              className="remote-lock-fob-modal__encoder-img"
            />
          </div>
          <div className="remote-lock-fob-modal__actions">
            <button className="button button-bordered auto-width" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button button-primary auto-width"
              type="button"
              onClick={handleNextFromPrepare}
            >
              Next
            </button>
          </div>
        </>
      )
    }

    if (currentStep === STEP.SELECT_FOB) {
      return (
        <>
          <div
            className={`remote-lock-fob-modal__select-content${isFobsLoading ? ' remote-lock-fob-modal__select-content--loading' : ''}`}
          >
            <p className="remote-lock-fob-modal__message">
              Please choose the correct card/fob number you are going to issue:
            </p>
            <div className="remote-lock-fob-modal__fob-select">
              <Select
                isClearable
                isSearchable
                options={availableFobs}
                value={selectedFob}
                isLoading={isFobsLoading}
                placeholder="Search..."
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={fobSelectStyles}
                onChange={(option: FobOption | null) => setSelectedFob(option)}
              />
            </div>
            <div className="remote-lock-fob-modal__refresh-row">
              <span className="remote-lock-fob-modal__hint">
                If your fob/card doesn't appear on the list, try refreshing.
              </span>
              <button
                type="button"
                className="button button-bordered auto-width remote-lock-fob-modal__refresh-btn"
                onClick={fetchAvailableFobs}
              >
                <ArrowsClockwiseIcon size={14} />
                Refresh list
              </button>
            </div>
          </div>
          {isFobsLoading && (
            <div className="remote-lock-fob-modal__loader-wrap">
              <Loader />
            </div>
          )}
          <div className="remote-lock-fob-modal__actions">
            <button className="button button-bordered auto-width" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className={`button button-primary auto-width${isFobsLoading ? ' remote-lock-fob-modal__btn--faded' : ''}`}
              type="button"
              disabled={!selectedFob || isFobsLoading}
              onClick={handleNextFromSelectFob}
            >
              Next
            </button>
          </div>
        </>
      )
    }

    if (currentStep === STEP.REVIEW) {
      return (
        <>
          <p className="remote-lock-fob-modal__message">Please wait, issuing the fob/card.</p>
          <div className="remote-lock-fob-modal__icon-area">
            <Loader />
          </div>
          <div className="remote-lock-fob-modal__actions">
            <button disabled className="button button-bordered auto-width" type="button">
              Cancel
            </button>
            <button disabled className="button button-primary auto-width" type="button">
              Next
            </button>
          </div>
        </>
      )
    }

    if (isSuccess) {
      return (
        <>
          <p className="remote-lock-fob-modal__message">
            <strong>Fob/card successfully issued!</strong>
            <span className="remote-lock-fob-modal__message-body">
              Place the fob/card again on the encoder until it gives a sound to save the changes.
              <br />
              Before you can use it to access the property, update your locks using the Remote Lock
              application.
            </span>
          </p>
          <div className="remote-lock-fob-modal__result remote-lock-fob-modal__result--success">
            <CheckIcon size={56} weight="bold" />
          </div>
          <div className="remote-lock-fob-modal__actions">
            <button className="button button-primary auto-width" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      )
    }

    return (
      <>
        <p className="remote-lock-fob-modal__message">
          <strong>Something went wrong!</strong>
          <span className="remote-lock-fob-modal__message-body">
            {errorMessage ??
              'We were not able to issue the fob/card. Please check your encoder and try again later.'}
          </span>
        </p>
        <div className="remote-lock-fob-modal__result remote-lock-fob-modal__result--error">
          <XIcon size={56} weight="bold" />
        </div>
        <div className="remote-lock-fob-modal__actions">
          <button className="button button-bordered auto-width" type="button" onClick={onClose}>
            Close
          </button>
          <button
            className="button button-primary auto-width"
            type="button"
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      </>
    )
  }

  return (
    <ModalContainer
      isVisible={isOpen}
      headerTitle="Issue physical fob/card"
      headerIcon={<BroadcastIcon size={20} />}
      closeable={!isProcessing}
      additionalClass="remote-lock-fob-modal-container"
      onClose={isProcessing ? undefined : onClose}
    >
      <div className="remote-lock-fob-modal">
        <Stepper currentStep={currentStep} steps={STEPS} />
        {renderContent()}
      </div>
    </ModalContainer>
  )
}

export default RemoteLockIssueFobModal
